import React from 'react';
import styled from 'styled-components';
import { 
  Focus, 
  Eye, 
  Smile, 
  User, 
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react';

const CardContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.75rem;
  border: 1px solid #e2e8f0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 1px 2px -1px rgba(0, 0, 0, 0.03);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, #3a5cf7, #8b5cf6);
    transform: scaleY(0);
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  &:hover {
    box-shadow: 0 10px 15px -3px rgba(58, 92, 247, 0.15), 0 4px 6px -2px rgba(58, 92, 247, 0.1);
    transform: translateY(-4px);
    border-color: rgba(58, 92, 247, 0.3);
    
    &::before {
      transform: scaleY(1);
    }
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const RegionBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.85rem;
  background: ${props => {
    const colors = {
      'nose': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'under_eyes': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'chin': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'lips': 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'jaw': 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
      'brow': 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      'skin': 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
    };
    return colors[props.region] || '#f3f4f6';
  }};
  color: white;
`;

const ConfidenceBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => {
    if (props.confidence === 'high') return '#10b981';
    if (props.confidence === 'medium') return '#f59e0b';
    return '#6b7280';
  }};
  color: white;
`;

const SurgicalBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background: #ef4444;
  color: white;
`;

const Finding = styled.p`
  color: #0f172a;
  font-size: 1.0625rem;
  line-height: 1.7;
  margin-bottom: 1.25rem;
  font-weight: 500;
  letter-spacing: -0.01em;
`;

const SuggestionsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  margin-bottom: 1rem;
`;

const SuggestionItem = styled.li`
  color: #475569;
  font-size: 0.9375rem;
  line-height: 1.7;
  margin-bottom: 0.75rem;
  padding-left: 1.75rem;
  position: relative;
  
  &:before {
    content: '→';
    position: absolute;
    left: 0;
    color: #3a5cf7;
    font-weight: bold;
    font-size: 1rem;
  }
`;

const EvidenceSection = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 0.75rem;
  margin-top: 1rem;
  font-size: 0.85rem;
  color: #6b7280;
`;

const getRegionIcon = (region) => {
  const icons = {
    'nose': Focus,  // Using Focus icon as Nose is not available in lucide-react
    'under_eyes': Eye,
    'lips': Smile,
    'chin': User,
    'jaw': User,
    'brow': Eye,
    'skin': AlertCircle
  };
  return icons[region] || AlertCircle;
};

const getDirectionIcon = (direction) => {
  if (direction === '>') return TrendingUp;
  if (direction === '<') return TrendingDown;
  return Minus;
};

function ConsultCard({ region, subtype, finding, evidence, suggestions, confidence, surgical }) {
  const RegionIcon = getRegionIcon(region);

  return (
    <CardContainer>
      <CardHeader>
        <RegionBadge region={region}>
          <RegionIcon size={16} />
          {region.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
        </RegionBadge>
        {confidence && (
          <ConfidenceBadge confidence={confidence}>
            {confidence} confidence
          </ConfidenceBadge>
        )}
        {surgical && (
          <SurgicalBadge>
            Surgical
          </SurgicalBadge>
        )}
      </CardHeader>
      
      <Finding>{finding}</Finding>
      
      {suggestions && suggestions.length > 0 && (
        <SuggestionsList>
          {suggestions.map((suggestion, index) => (
            <SuggestionItem key={index}>
              {suggestion}
            </SuggestionItem>
          ))}
        </SuggestionsList>
      )}
      
      {evidence && evidence.metric && (
        <EvidenceSection>
          <strong>Evidence:</strong> {evidence.metric}
          {evidence.value !== undefined && (
            <> = {typeof evidence.value === 'number' ? evidence.value.toFixed(2) : evidence.value}</>
          )}
          {evidence.threshold !== undefined && evidence.direction && (
            <> {evidence.direction} {evidence.threshold.toFixed(2)}</>
          )}
        </EvidenceSection>
      )}
    </CardContainer>
  );
}

export default ConsultCard;

